// Cliente HTTP da API /api/v1. Erros do servidor viram ApiError com código e detalhes por campo.

export interface FieldIssue {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
  get fieldIssues(): FieldIssue[] {
    return Array.isArray(this.details) ? (this.details as FieldIssue[]) : [];
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${url}`, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Sem conexão com o servidor. Verifique a internet e tente novamente.');
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = data?.error ?? {};
    if (res.status === 401 && url !== '/auth/login' && url !== '/auth/me') window.dispatchEvent(new Event('tn:unauthorized'));
    throw new ApiError(res.status, err.code ?? 'error', err.message ?? `Erro ${res.status}`, err.details);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body: unknown = {}) => request<T>('POST', url, body),
  put: <T>(url: string, body: unknown) => request<T>('PUT', url, body),
  patch: <T>(url: string, body: unknown) => request<T>('PATCH', url, body),
  del: <T>(url: string) => request<T>('DELETE', url),
};

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))}` : '';
}

// ------------------------------------------------------------------ tipos compartilhados

export interface Me {
  id: string;
  email: string;
  isAdmin: boolean;
  mustChangePassword: boolean;
  consultantId: string;
  consultantName: string;
}

export interface Paged<T> {
  data: T[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface Consultant {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  active: boolean;
  isDemo: boolean;
  version: number;
  userId: string | null;
  userActive: boolean | null;
}

export type ContactPurpose = 'agenda' | 'relatorio' | 'extrato' | 'cobranca';

export interface Contact {
  id: string;
  clientId: string;
  name: string;
  role: string | null;
  email: string | null;
  whatsapp: string | null;
  purposes: ContactPurpose[];
  active: boolean;
  version: number;
}

export interface Client {
  id: string;
  code: string;
  name: string;
  tradeName: string | null;
  documentType: 'cpf' | 'cnpj' | null;
  document: string | null;
  address: string | null;
  notes: string | null;
  active: boolean;
  isDemo: boolean;
  version: number;
}

export interface ClientDetail extends Client {
  contacts: Contact[];
  projects: { id: string; code: string; name: string; status: ProjectStatus }[];
}

export type ProjectStatus = 'ativo' | 'pausado' | 'encerrado' | 'cancelado';

export interface ProjectRow {
  id: string;
  code: string;
  name: string;
  status: ProjectStatus;
  startDate: string | null;
  endDate: string | null;
  isDemo: boolean;
  clientId: string;
  clientName: string;
  clientTradeName: string | null;
  ownerConsultantId: string;
  ownerName: string;
  linkedToMe: boolean;
  phaseId: string | null;
  phaseName: string | null;
  phaseColor: string | null;
}

export interface ProjectPhase {
  id: string;
  name: string;
  sortOrder: number;
  color: string;
  active: boolean;
  version: number;
  projectCount?: number;
}

export interface KanbanCard {
  id: string;
  code: string;
  name: string;
  status: ProjectStatus;
  version: number;
  phaseId: string | null;
  phaseChangedAt: string | null;
  startDate: string | null;
  endDate: string | null;
  isDemo: boolean;
  clientName: string;
  clientTradeName: string | null;
  ownerName: string;
  linkedToMe: boolean;
}

export type Modality = 'avulso' | 'banco_horas' | 'valor_fechado' | 'mensalidade';

export interface Contract {
  id: string;
  projectId: string;
  parentContractId: string | null;
  code: string;
  modality: Modality;
  startDate: string;
  endDate: string | null;
  hourlyRate: string | null;
  contractedMinutes: number | null;
  totalValue: string | null;
  monthlyValue: string | null;
  overageRule: 'pendencia' | 'avulso_autorizado';
  lowBalanceMinutes: number | null;
  externalReference: string | null;
  status: 'ativo' | 'encerrado' | 'cancelado';
  notes: string | null;
  version: number;
}

export interface ProjectDetail {
  id: string;
  code: string;
  clientId: string;
  clientName: string;
  clientTradeName: string | null;
  name: string;
  scope: string | null;
  ownerConsultantId: string;
  ownerName: string;
  startDate: string | null;
  endDate: string | null;
  status: ProjectStatus;
  notes: string | null;
  isDemo: boolean;
  version: number;
  participants: { id: string; name: string }[];
  contracts: Contract[];
  linkedToMe: boolean;
  phaseId: string | null;
  phaseChangedAt: string | null;
  phase: ProjectPhase | null;
  phaseHistory: { id: string; changedAt: string; note: string | null; fromName: string | null; toName: string | null; changedByName: string | null }[];
}

export interface Lookup {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  version: number;
  kind?: 'client' | 'internal' | 'personal';
}

export interface Organization {
  id: string;
  name: string;
  legalName: string | null;
  cnpj: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  signature: string | null;
  isDemo: boolean;
  version: number;
}

export interface UserRow {
  id: string;
  email: string;
  isAdmin: boolean;
  active: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  consultantId: string;
  consultantName: string;
  lastInviteAt: string | null;
}

// ------------------------------------------------------------------ P2: agenda e execuções

export type AppointmentStatus = 'agendado' | 'confirmado' | 'realizado' | 'cancelado' | 'reagendado' | 'nao_compareceu';
export type ServiceModality = 'remoto' | 'presencial';

export interface Appointment {
  id: string;
  private: boolean;
  title: string;
  startsAt: string;
  endsAt: string;
  status: AppointmentStatus;
  typeId: string | null;
  typeName: string | null;
  typeKind: 'client' | 'internal' | 'personal' | null;
  clientId: string | null;
  clientName: string | null;
  projectId: string | null;
  projectName: string | null;
  modality: ServiceModality | null;
  plannedCategoryId: string | null;
  plannedAreaId: string | null;
  plannedBreakMinutes: number;
  location: string | null;
  meetingLink: string | null;
  description: string | null;
  rescheduledFromId: string | null;
  rescheduledToId: string | null;
  outcomeAt: string | null;
  outcomeNotes: string | null;
  version: number;
  participants: { id: string; name: string }[];
  contacts: { id: string; name: string }[];
}

export type ExecutionStatus = 'rascunho' | 'pronto' | 'confirmado' | 'anulado';

export interface ExecutionRow {
  id: string;
  status: ExecutionStatus;
  serviceDate: string | null;
  durationMinutes: number | null;
  quickNote: string | null;
  summary: string | null;
  clientName: string | null;
  projectName: string | null;
  executorConsultantId: string;
  executorName: string;
  updatedAt: string;
}

export interface Allocation {
  categoryId: string;
  areaId: string | null;
  minutes: number;
  categoryName?: string;
  areaName?: string | null;
}

export interface SummaryRun {
  id: string;
  status: 'processando' | 'gerado' | 'aplicado' | 'recusado' | 'falhou';
  provider: string;
  suggestedText: string | null;
  appliedText: string | null;
  uncertainPoints: string[] | null;
  error: string | null;
  sourceVersion: number;
  stale: boolean;
  createdAt: string;
}

export interface ExecutionDetail {
  id: string;
  clientDraftId: string | null;
  appointmentId: string | null;
  clientId: string | null;
  clientName: string | null;
  projectId: string | null;
  projectName: string | null;
  projectCode: string | null;
  projectOwnerId: string | null;
  projectOwnerName: string | null;
  contractId: string | null;
  contractCode: string | null;
  contractModality: Modality | null;
  contractHourlyRate: string | null;
  executorConsultantId: string;
  executorName: string | null;
  authorConsultantId: string;
  authorName: string | null;
  serviceDate: string | null;
  modality: ServiceModality | null;
  startedAt: string | null;
  endedAt: string | null;
  breakMinutes: number;
  durationMinutes: number | null;
  durationMethod: 'horarios' | 'informada' | null;
  quickNote: string | null;
  summary: string | null;
  summaryOrigin: 'manual' | 'ia_revisada' | null;
  status: ExecutionStatus;
  confirmedAt: string | null;
  voidReason: string | null;
  version: number;
  allocations: (Allocation & { id: string; categoryName: string })[];
  source: { id: string; kind: SourceKind; fileName: string | null; normalizedText: string; textVersion: number } | null;
  summaryRuns: SummaryRun[];
  attachments: { id: string; fileName: string; mimeType: string; sizeBytes: number; createdAt: string }[];
  ai: { configured: boolean; provider: string | null; simulator: boolean };
  billingCondition: 'a_definir' | 'coberto_banco' | 'faturavel' | 'mensalidade' | 'valor_fechado' | 'nao_faturavel';
  coveredMinutes: number | null;
  overageMinutes: number;
  overageStatus: 'nenhum' | 'pendente' | 'cobrado_avulso' | 'sem_cobranca' | 'coberto_credito';
  coverageMode: 'padrao' | 'somente_producao';
  contractBalance: number | null;
  movements: { id: string; kind: string; minutes: number; occurredOn: string; reason: string | null }[];
  billingItems: { id: string; sourceType: string; total: string; quantity: string; unitPrice: string; billingStatus: string; transportStatus: string; reviewStatus: string; version: number }[];
}

export type SourceKind = 'digitado' | 'colado' | 'txt' | 'srt' | 'vtt';

// ------------------------------------------------------------------ P3: horas e cobranças

export interface BankSummary {
  initialCredit: number;
  openingBalance: number;
  additionalCredits: number;
  credits: number;
  consumed: number;
  reversed: number;
  adjustments: number;
  balance: number;
  hasInitialCredit: boolean;
  pendingOverageMinutes: number;
  pendingOverageCount: number;
}

export interface HourBankRow extends BankSummary {
  id: string;
  code: string;
  status: string;
  contractedMinutes: number | null;
  lowBalanceMinutes: number | null;
  hourlyRate: string | null;
  projectId: string;
  projectName: string;
  clientName: string;
  ownerName: string;
  lowBalance: boolean;
}

export interface HourMovement {
  id: string;
  kind: 'credito_inicial' | 'saldo_abertura' | 'credito_adicional' | 'consumo' | 'estorno' | 'ajuste';
  minutes: number;
  occurredOn: string;
  executionId: string | null;
  hourlyRate: string | null;
  alreadyInvoiced: boolean | null;
  reference: string | null;
  reason: string | null;
  executionDate: string | null;
  executorName: string | null;
  executionSummary: string | null;
  balanceAfter: number;
}

export interface BankStatement {
  contract: Contract & { projectName: string; clientName: string };
  summary: BankSummary;
  movements: HourMovement[];
  pendingOverages: { id: string; serviceDate: string; overageMinutes: number; executorName: string }[];
}

export type BillingQueue = 'pendente_conferencia' | 'pronto_envio' | 'enviado' | 'falha' | 'faturado' | 'cancelado';

export interface BillingItem {
  id: string;
  sourceType: 'execution' | 'overage' | 'contract_initial' | 'contract_addition' | 'monthly' | 'installment';
  sourceId: string;
  clientId: string;
  clientName: string;
  projectId: string;
  projectName: string;
  contractId: string;
  contractCode: string;
  competence: string;
  serviceDate: string | null;
  description: string;
  durationMinutes: number | null;
  quantity: string;
  unit: 'hour' | 'package' | 'month' | 'installment';
  unitPrice: string;
  total: string;
  currency: string;
  reviewStatus: 'pendente_conferencia' | 'pronto_envio';
  transportStatus: 'nao_enviado' | 'aguardando' | 'enviado' | 'falhou';
  billingStatus: 'nao_faturado' | 'faturado' | 'cancelado' | 'ajustado';
  receiptStatus: 'nao_informado' | 'recebido';
  externalReference: string | null;
  transportError: string | null;
  transportAttempts: number;
  version: number;
}

export interface Installment {
  id: string;
  description: string;
  dueDate: string;
  amount: string;
  billingItemId: string | null;
  billingStatus: BillingItem['billingStatus'] | null;
}

// ------------------------------------------------------------------ P4: saídas e mensagens

export type MessagePurpose = 'confirmacao_agenda' | 'lembrete' | 'reagendamento' | 'cancelamento' | 'relatorio_sessao' | 'extrato';
export type MessageStatus = 'preparado' | 'em_processamento' | 'aceito' | 'falhou' | 'entregue' | 'preparado_aberto' | 'invalidado';

export interface MessageRecipient {
  contactId: string;
  name: string;
  email: string | null;
  whatsapp: string | null;
}

export interface MessageRow {
  id: string;
  purpose: MessagePurpose;
  channel: 'email' | 'whatsapp';
  clientId: string;
  clientName: string;
  subject: string;
  status: MessageStatus;
  statusReason: string | null;
  lastSentAt: string | null;
  sendCount: number;
  updatedAt: string;
  appointmentId: string | null;
  executionId: string | null;
  contractId: string | null;
}

export interface MessageDetail extends Omit<MessageRow, 'clientName'> {
  recipients: MessageRecipient[];
  body: string;
  attachments: { snapshotId: string; kind: string; label: string }[];
  contentHash: string;
  lastSentHash: string | null;
  version: number;
  templateVersion: number | null;
  problems: string[];
  attempts: { id: string; status: string; provider: string; channel: string; error: string | null; createdAt: string; contentHash: string; recipients: MessageRecipient[] }[];
  availableContacts: { id: string; name: string; email: string | null; whatsapp: string | null; purposes: ContactPurpose[] }[];
  preview: { html: string };
  whatsappLinks: { name: string; url: string }[];
  whatsappMode: 'link' | 'cloud_api';
}

export interface MessageTemplate {
  id: string;
  purpose: MessagePurpose;
  name: string;
  subject: string;
  body: string;
  isDefault: boolean;
  active: boolean;
  version: number;
}

export interface IntegrationsStatus {
  email: { configured: boolean; provider: string | null; simulator: boolean };
  whatsapp: { mode: string; note: string };
  ai: { configured: boolean; provider: string | null; model?: string | null; simulator: boolean };
  financial: { configured: boolean; note: string; provider?: string };
}
